import React, { useEffect, useState, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { toPersianDigits } from '../../utils/convertNumbers';
import { CustomAdapterDateFnsJalali, usePersianDatePicker } from '../../hooks/usePersianDatePicker';

const PersianMonthPicker = ({ 
    value, 
    onChange, 
    label = "ماه و سال", 
    helperText = "",
    format = "yyyy/MM",
    disabled = false,
    readOnly = true,
    error = false,
    required = false,
    ...props 
}) => {
    const { convertAllElements } = usePersianDatePicker();

    // Control the open state so clicking anywhere on the field (not just the
    // calendar icon) opens the picker. The input is readOnly, so a click can't
    // start text entry — it only triggers the calendar.
    const [open, setOpen] = useState(false);

    const handleChange = useCallback((date) => {
        // Propagate both year and month selections so consumers refresh immediately when the
        // year changes (previously only month selection committed, so a year change was
        // swallowed until the user also re-picked a month). Consumers must normalize to the
        // first day of the selected Jalali month — setting day=1 on the Gregorian date can
        // shift the Jalali month backward (ClassroomInfoGraph's handlers do this).
        onChange?.(date);
    }, [onChange]);

    // Effect to override placeholder - MUI MonthPicker generates it from format
    useEffect(() => {
        const targetPlaceholder = 'ماه / سال';
        
        const overridePlaceholder = () => {
            // Find all month picker inputs
            const inputs = document.querySelectorAll('input[type="text"]');
            inputs.forEach(input => {
                const isMonthPickerInput = input.hasAttribute('readonly') || 
                                         input.closest('.MuiMonthPicker-root') || 
                                         input.closest('[class*="MonthPicker"]') ||
                                         input.closest('[class*="MuiPickers"]');
                
                if (isMonthPickerInput) {
                    const currentPlaceholder = input.getAttribute('placeholder') || input.placeholder || '';
                    // Override if it contains format patterns or is empty
                    if (!currentPlaceholder || 
                        currentPlaceholder.includes('YYYY') || 
                        currentPlaceholder.includes('yyyy') ||
                        currentPlaceholder.includes('MM') ||
                        currentPlaceholder.match(/\d{4}/)) {
                        input.setAttribute('placeholder', targetPlaceholder);
                        input.placeholder = targetPlaceholder;
                    }
                }
            });
        };

        // Override immediately and on intervals
        overridePlaceholder();
        const intervalId = setInterval(overridePlaceholder, 100);
        
        // Also override on focus events
        const handleFocus = (e) => {
            if (e.target && e.target.tagName === 'INPUT') {
                setTimeout(() => overridePlaceholder(), 10);
            }
        };
        document.addEventListener('focusin', handleFocus, true);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('focusin', handleFocus, true);
        };
    }, [value]);

    return (
        <LocalizationProvider 
            dateAdapter={CustomAdapterDateFnsJalali}
        >
            <DatePicker
                label={label}
                value={value}
                onChange={handleChange}
                format={format}
                disabled={disabled}
                views={['year', 'month']}
                openTo="month"
                open={open}
                // Merge slotProps: our textField config takes priority, then merge any props.slotProps
                slotProps={{
                    ...(props.slotProps || {}),
                    popper: {
                        sx: {
                            // Center year labels and the selected highlight pill in year selection view
                            '& .MuiYearCalendar-root .MuiPickersYear-yearButton': {
                                justifyContent: 'center',
                                textAlign: 'center',
                            },
                            '& .MuiYearCalendar-root .MuiPickersYear-yearButton > *': {
                                margin: '0 auto',
                                textAlign: 'center',
                            },
                            // Same centering for month labels in the month selection view
                            '& .MuiMonthCalendar-root .MuiPickersMonth-monthButton': {
                                justifyContent: 'center',
                                textAlign: 'center',
                            },
                            '& .MuiMonthCalendar-root .MuiPickersMonth-monthButton > *': {
                                margin: '0 auto',
                                textAlign: 'center',
                            },
                        },
                        ...(props.slotProps?.popper || {}),
                    },
                    textField: {
                        variant: 'outlined',
                        fullWidth: true,
                        size: 'small',
                        helperText: helperText,
                        error: error,
                        required: required,
                        // Open the picker when the field itself is clicked, not just the icon.
                        onClick: () => { if (!disabled) setOpen(true); },
                        sx: {
                            textAlign: 'right',
                            '& .MuiInputBase-root, & input': { cursor: 'pointer' },
                        },
                        placeholder: "ماه / سال",
                        inputProps: {
                            readOnly: readOnly,
                        },
                        InputProps: {
                            readOnly: readOnly,
                            // Custom input component to display Persian digits
                            inputComponent: React.forwardRef(function PersianInputComponent(inputProps, ref) {
                                const { value, ...other } = inputProps;
                                const persianValue = value ? toPersianDigits(value) : '';
                                return (
                                    <input
                                        {...other}
                                        ref={ref}
                                        value={persianValue}
                                        readOnly={readOnly}
                                    />
                                );
                            }),
                        },
                        ...(props.slotProps?.textField || {}),
                    },
                }}
                onOpen={() => {
                    setOpen(true);
                    setTimeout(() => convertAllElements(), 50);
                    setTimeout(() => convertAllElements(), 150);
                }}
                onClose={() => {
                    setOpen(false);
                    setTimeout(() => convertAllElements(), 50);
                }}
                onViewChange={(view) => {
                    setTimeout(() => convertAllElements(), 50);
                    setTimeout(() => convertAllElements(), 150);
                    props.onViewChange?.(view);
                }}
                // Extract slotProps from props to avoid overriding our config
                {...(() => {
                    const { slotProps: _, onViewChange: __, ...restProps } = props;
                    return restProps;
                })()}
            />
        </LocalizationProvider>
    );
};

export default PersianMonthPicker;

